import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import type {
  EnvironmentKey,
  ConfigEntity,
  DomainEntity,
  EntityRootData,
  OptionalOperationType,
  ConfigEntityRootData,
  ConfigEntityPatchEndpointResponse,
} from '@web-config-app/core';
import {
  isConfigEntity,
  operationToConfigEntityAuthAction,
} from '@web-config-app/core';
import {
  API_JSON_CONTENT_TYPE,
  buildConfigEntityAuthHeaders,
} from '@web-config-app/api';
import { getEntityEndpoint } from '../../utilities/get-entity-endpoint/get-entity-endpoint.util';
import { OnMutateWithOperation } from '../../types/operation.types';
import { useEnvironmentFetch } from '../use-environment-fetch/use-environment-fetch';
import { useUserInfo } from '../use-user-info/use-user-info';
import { prepareEntityBodyData } from '../../utilities/prepare-entity-body-data/prepare-entity-body-data';
import { assertCompletionCriteriaPublishable } from '../../utilities/assert-completion-criteria-publishable/assert-completion-criteria-publishable';

export type UseEntityUpdateResult = ReturnType<typeof useEntityUpdate>;

export type UseEntityUpdateOptions = Pick<
  UseMutationOptions<ConfigEntityPatchEndpointResponse, Error, void>,
  'onError' | 'onSettled'
> & {
  onMutate?: OnMutateWithOperation;
  onSuccess?: (
    data: ConfigEntityPatchEndpointResponse,
    environment: EnvironmentKey,
  ) => void;
  fetchFn?: (path: string, options: any) => Promise<Response>;
};

interface UseEntityUpdateProps {
  entity: ConfigEntity | DomainEntity;
  instanceId: string;
  environment: EnvironmentKey;
  operation: Exclude<OptionalOperationType, 'custom'>;
  data?: EntityRootData | ConfigEntityRootData;
  headers?: HeadersInit;
  options?: UseEntityUpdateOptions;
}

/**
 * useEntityUpdate - The following hook is responsible for calling the BE API associated to the all Update (PATCH) services of an entity.
 * @param props
 * @param props.entity - the whole {@link Entity} that exposes the needed values including the id, endpoints, requiredFieldValues etc.
 * @param props.instanceId - the entity instance ID needed to associate the form data with to saving an existing entity. Not needed for saving a new entity.
 * @param props.data - the root data of the form used when saving the entity data
 * @param props.headers - optional headers to pass into the fetch function
 * @param props.options - optional config object
 * @param props.options.fetchFn - You can pass in your own custom fetch function to be used, instead of the default leagueFetch
 * @returns the result of useMutation, which is then used by our EntityDetailsProvider, who is responsible for relaying the result where needed.
 * @returns enableAction - if the operation should exist or not
 */
export const useEntityUpdate = ({
  entity,
  instanceId,
  operation,
  environment,
  data,
  headers = {
    'Content-Type': API_JSON_CONTENT_TYPE,
  },
  options,
}: UseEntityUpdateProps) => {
  const { endpoints } = entity;
  const patchOperation = endpoints[operation];
  const { apiUrl, generateDomainEntityId } = isConfigEntity(entity)
    ? entity.domainEntity
    : entity;

  let mutationFn;
  const currentEnvironmentFetch = useEnvironmentFetch(environment, apiUrl);
  const { userInfo } = useUserInfo();
  // we only want to set the mutationFn if the patchOperation exists. Not every entity will have every patch operation.
  if (patchOperation) {
    // the path parameters name value is used in the params prop of endpointFetch
    const entityInstanceIdParam = patchOperation?.pathParameters?.find(
      (param) => patchOperation.path.indexOf(`${param.name}`) > -1,
    );
    const entityInstanceIdParamName = entityInstanceIdParam?.name;

    const params = [{ name: entityInstanceIdParamName!, value: instanceId }];

    const { endpointFetch } =
      getEntityEndpoint<ConfigEntityPatchEndpointResponse>(
        patchOperation,
        options?.fetchFn ?? currentEnvironmentFetch,
      );

    const shouldInjectDomainEntityId =
      generateDomainEntityId?.enabled && entity.type === 'config-entity';

    const domainEntityType = isConfigEntity(entity)
      ? entity.domainEntity.type
      : entity.type;
    const action = operationToConfigEntityAuthAction[operation];

    mutationFn = () => {
      if (operation === 'publish' || operation === 'republish') {
        assertCompletionCriteriaPublishable({ data, entity });
      }

      const bodyData = prepareEntityBodyData({
        data,
        entity,
        operationEndpoint: patchOperation,
        userInfo,
        shouldInjectDomainEntityId,
        generateDomainEntityId,
      });

      const body = JSON.stringify({
        data: bodyData,
      });

      return endpointFetch({
        params,
        body,
        headers: {
          ...headers,
          ...buildConfigEntityAuthHeaders(domainEntityType, action),
        },
      });
    };
  }

  const mutation = useMutation<ConfigEntityPatchEndpointResponse, Error, void>({
    mutationFn,
    onMutate: () => options?.onMutate?.(operation),
    onSuccess: (responseData) =>
      options?.onSuccess?.(responseData, environment),
    onError: options?.onError,
    onSettled: options?.onSettled,
  });

  return {
    ...mutation,
    actionLabel: patchOperation?.actionLabel,
    actionType: patchOperation?.actionType,
    enableAction: !!patchOperation,
  };
};
